import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { Loader, Location, Table } from "@/components"
import { SpotRateListingType, SpotRateType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import {
  convertEquipmentType,
  dateFormat1,
  dollarUS,
  formatDryvan,
} from "@/lib/services"
import { TrashIcon, ArrowPathIcon } from "@heroicons/react/24/outline"
import Swal from "sweetalert2"

type Props = {
  popup: boolean
  isLoading: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: SpotRateListingType[]
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: SpotRateType) => void
  handleFormDelete: (form: string) => void
  handleUpdateForm: (form: { id: string; createdAt: string }) => void
}

export const SpotRate: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<SpotRateType>({
    defaultValues: {
      origin: {
        city: "",
        state: "",
        zip: "",
        country: "",
        location: {
          lat: 0,
          lng: 0,
        },
      },
      destination: {
        city: "",
        state: "",
        zip: "",
        country: "",
        location: {
          lat: 0,
          lng: 0,
        },
      },
      equipmentType: "",
      carrierCost: undefined,
    },
  })

  const onSubmit = (data: SpotRateType) => {
    const spotRate = data
    props.handleForm(spotRate)
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  useResetOnClose(props.popup, () => {
    reset()
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title="Add Spot Rate"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-1">
                <input
                  className="hidden vehicle_radio_customize"
                  type="radio"
                  id="vehicle_card"
                  value="DRY_VAN"
                  {...register("equipmentType", { required: true })}
                />
                <label htmlFor="vehicle_card">
                  <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
                </label>
              </div>
              <div className="col-span-1">
                <input
                  className="hidden vehicle_radio_customize"
                  type="radio"
                  id="vehicle_card1"
                  value="FLATBED"
                  {...register("equipmentType", { required: true })}
                />
                <label htmlFor="vehicle_card1">
                  <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs dark:text-primary-8 text-light-1">Origin</label>
                <Location
                  sportRate={true}
                  setData={(data) => {
                    setValue("origin.city", data.city)
                    setValue("origin.state", data.state)
                    setValue("origin.zip", data.zip)
                    setValue("origin.country", data.country)
                    setValue("origin.location.lat", data.latLng.lat)
                    setValue("origin.location.lng", data.latLng.lng)
                  }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs dark:text-primary-8 text-light-1">Destination</label>
                <Location
                  sportRate={true}
                  setData={(data) => {
                    setValue("destination.city", data.city)
                    setValue("destination.state", data.state)
                    setValue("destination.zip", data.zip)
                    setValue("destination.country", data.country)
                    setValue("destination.location.lat", data.latLng.lat)
                    setValue("destination.location.lng", data.latLng.lng)
                  }}
                />
              </div>
            </div>

            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Carrier Cost</label>
              <input
                className={
                  (errors.carrierCost ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white mt-1 outline-none dark:text-white text-light-2 border-2 rounded  placeholder:text-zinc-500 placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Carrier Cost"
                {...register("carrierCost", { required: true })}
              />
            </fieldset>
          </div>
          <div
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 flex dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
          >
            {props.isLoading && <Loader size="small" />}
            <button className="flex justify-center w-full" type="button">
              Submit
            </button>
          </div>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.map((e, i) => (
            <tr
              key={i}
              className={
                (e.active ? "bg-green-900/20" : "bg-red-900/20") + " text-left"
              }
            >
              <td className={"whitespace-nowrap p-4 w-40"}>
                {e.type == null
                  ? "-"
                  : e.type == "Southbound"
                  ? "Southbound"
                  : e.type == "Northbound"
                  ? "Northbound"
                  : null}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {formatDryvan(e.equipmentType)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52 truncate"}>
                {e.origin == null ? "-" : e.origin}
              </td>
              <td className={"whitespace-nowrap p-4 w-52 truncate"}>
                {e.destination == null ? "-" : e.destination}
              </td>
              <td className={"whitespace-nowrap p-4 w-52 truncate"}>
                {e.originMarket}
              </td>
              <td className={"whitespace-nowrap p-4 w-52 truncate"}>
                {e.destinationMarket}
              </td>
              <td className={"whitespace-nowrap p-4 w-36"}>
                {dollarUS.format(e.carrierCost)}
              </td>
              <td className={"whitespace-nowrap p-4 w-40"}>
                {dateFormat1(e.createdAt)}
              </td>
              <td className={"whitespace-nowrap p-4 w-40"}>
                {e.updateBy.firstName} {e.updateBy.lastName}
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <ArrowPathIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => {
                      const date = new Date()
                      const formatDate = date.toISOString()
                      props.handleUpdateForm({
                        id: e.id,
                        createdAt: formatDate,
                      })
                    }}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
